import { useHeaderStore } from 'src/stores/HeaderStore';
import styles from './ChangePasswordModal.module.scss';
import { Button, Input, Modal, Toast } from '@ds';
import { useState, useMemo, useEffect, useRef } from 'react';
import zxcvbn from 'zxcvbn';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { useChangePassword } from 'src/shared/mutations/useChangePassword';

interface APIError {
  response?: {
    data?: {
      errorCode?: number;
    };
  };
}

enum ChangePasswordErrorCode {
  IncorrectOldPassword = 1009,
  UserNotFound = 1002,
  WeakNewPassword = 1010,
  ReusedPassword = 1013,
}

const MODAL_TITLE = 'CHANGE PASSWORD';
const SUB_REQUIREMENTS_TOTAL = 4;
const MIN_LENGTH = 12;
const PASSWORD_STRENGTH_MEDIUM_STANDARD = 2;

const ChangePasswordModal = () => {
  const { isChangePasswordModalOpen, closeChangePasswordModal } = useHeaderStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const [isCurrentPasswordError, setIsCurrentPasswordError] = useState(false);
  const [isNewPasswordError, setIsNewPasswordError] = useState(false);
  const toastrRef = useRef<IToastr>(null);
  const { mutate: changePasswordMutation, isPending: isSaving } = useChangePassword();

  useEffect(() => {
    if (!isChangePasswordModalOpen) {
      setSaveErrorMessage('');
      setIsCurrentPasswordError(false);
      setIsNewPasswordError(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    }
  }, [isChangePasswordModalOpen]);

  useEffect(() => {
    if (isCurrentPasswordError) {
      setIsCurrentPasswordError(false);
    }
  }, [currentPassword]);

  useEffect(() => {
    if (isNewPasswordError) {
      setIsNewPasswordError(false);
    }
  }, [newPassword]);

  const savePassword = () => {
    changePasswordMutation(
      { oldPassword: currentPassword, newPassword },
      {
        onSuccess: () => {
          toastrRef.current?.show({ message: 'Your password was successfully changed', severity: 'success' });
          closeChangePasswordModal();
        },
        onError: (e) => {
          const error = e as APIError;
          let errorMessage: string;
          switch (error.response?.data?.errorCode) {
            case ChangePasswordErrorCode.IncorrectOldPassword:
              errorMessage = 'Looks like your old password is not correct';
              setIsCurrentPasswordError(true);
              break;
            case ChangePasswordErrorCode.UserNotFound:
              errorMessage = 'Unable to find the user.';
              break;
            case ChangePasswordErrorCode.WeakNewPassword:
              errorMessage = 'The new password is not strong.';
              setIsNewPasswordError(true);
              break;
            case ChangePasswordErrorCode.ReusedPassword:
              errorMessage = 'You cannot reuse any of your past ten passwords. Please use a new password';
              setIsNewPasswordError(true);
              break;
            default:
              errorMessage = 'Change password failed!';
          }
          setSaveErrorMessage(errorMessage);
        },
      }
    );
  };

  const hasMinLength = useMemo(() => newPassword.length >= MIN_LENGTH, [newPassword]);
  const hasLowerCase = useMemo(() => /[a-z]/.test(newPassword), [newPassword]);
  const hasUpperCase = useMemo(() => /[A-Z]/.test(newPassword), [newPassword]);
  const hasNumber = useMemo(() => /[0-9]/.test(newPassword), [newPassword]);
  const hasSymbol = useMemo(() => /[()'\-!@#$%^&*+=|[\]{};:<>,.?/]/.test(newPassword), [newPassword]);

  const subRequirementsMet = [hasLowerCase, hasUpperCase, hasNumber, hasSymbol].filter(Boolean).length;
  const allSubRequirementsMet = subRequirementsMet === SUB_REQUIREMENTS_TOTAL;

  const passwordStrength = useMemo(() => {
    if (!newPassword) return 0;
    return zxcvbn(newPassword).score;
  }, [newPassword]);

  const allCriteriaMet = hasMinLength && allSubRequirementsMet;

  const shouldShowErrorForNewPassword = !!newPassword && !allCriteriaMet;

  const shouldShowErrorForConfirmNewPassword = newPassword !== confirmNewPassword && confirmNewPassword !== '';

  const passwordsMatch = newPassword === confirmNewPassword && newPassword !== '';

  const passwordNotStrongEnough =
    !!newPassword && allCriteriaMet && passwordStrength < PASSWORD_STRENGTH_MEDIUM_STANDARD;

  const canSave =
    allCriteriaMet &&
    passwordsMatch &&
    passwordStrength >= PASSWORD_STRENGTH_MEDIUM_STANDARD &&
    !isCurrentPasswordError &&
    !isNewPasswordError;

  const getRequirementClassName = (isMet: boolean) => {
    return isMet ? `${styles['requirement']} ${styles['met']}` : styles['requirement'];
  };

  const getStrengthBarColor = (barIndex: number) => {
    if (barIndex > passwordStrength) return '';

    if (passwordStrength < PASSWORD_STRENGTH_MEDIUM_STANDARD) return styles['weak'];
    if (passwordStrength === PASSWORD_STRENGTH_MEDIUM_STANDARD) return styles['medium'];
    return styles['strong'];
  };

  return (
    <>
      <Modal
        visible={isChangePasswordModalOpen}
        onModalClosed={closeChangePasswordModal}
        className={styles['change-password-modal']}
        title={MODAL_TITLE}
        showLoadingOverlay={isSaving}
        modalClassName={styles['modal']}
      >
        <div className={styles['content']}>
          {saveErrorMessage && <div className={styles['api-error-message']}>{saveErrorMessage}</div>}
          <div className={styles['main-content']}>
            <div className={styles['inputs-group']}>
              <Input
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                label="Current Password"
                type="password"
                required
                hideErrorMessage
                showEyeIcon
                showErrorBorder={isCurrentPasswordError}
              />
              <div>
                <Input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  label="New Password"
                  type="password"
                  required
                  hideErrorMessage
                  showEyeIcon
                  showErrorBorder={shouldShowErrorForNewPassword || isNewPasswordError}
                />
                {newPassword && (
                  <div className={styles['password-strength-section']}>
                    <span className={styles['label']}>Password Strength</span>
                    <div className={styles['strength-meter']}>
                      {[...Array(5)].map((_, index) => (
                        <div
                          key={index}
                          className={`${styles['strength-meter-bar']} ${getStrengthBarColor(index)}`}
                        ></div>
                      ))}
                    </div>
                  </div>
                )}
                {shouldShowErrorForNewPassword && (
                  <div className={styles['input-error-message']}>
                    Looks like your new password doesn't comply with all the criteria
                  </div>
                )}
                {passwordNotStrongEnough && (
                  <div className={styles['input-error-message']}>Looks like your new password isn’t strong enough</div>
                )}
              </div>
              <div>
                <Input
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  label="Confirm New Password"
                  type="password"
                  required
                  hideErrorMessage
                  showEyeIcon
                  showErrorBorder={shouldShowErrorForConfirmNewPassword}
                />
                {shouldShowErrorForConfirmNewPassword && (
                  <div className={styles['input-error-message']}>Looks like your new passwords do not match</div>
                )}
              </div>
            </div>
            <div className={styles['criteria-group']}>
              <div className={styles['requirement-title']}>Please make sure your password contains:</div>
              <div className={getRequirementClassName(hasMinLength)}>Minimum of {MIN_LENGTH} characters</div>
              <div className={getRequirementClassName(subRequirementsMet === SUB_REQUIREMENTS_TOTAL)}>
                All 4 of the following 4 criteria:
              </div>
              <div className={styles['sub-requirements-group']}>
                <div className={getRequirementClassName(hasLowerCase)}>at least one lower case letter</div>
                <div className={getRequirementClassName(hasUpperCase)}>at least one upper case letter</div>
                <div className={getRequirementClassName(hasNumber)}>at least one number</div>
                <div className={getRequirementClassName(hasSymbol)}>
                  {"at least one symbol () ' - ! @ # $ % ^& * - + = |  [ ] { } : ; < > , . ? /"}
                </div>
              </div>
            </div>
          </div>
          <Button
            className="w-full md:!w-[180px] mt-[36px] float-right mr-[24px] md:mr-[unset]"
            variant="contained"
            styleType="success"
            size="large"
            disabled={!canSave}
            onClick={savePassword}
          >
            SAVE
          </Button>
        </div>
      </Modal>
      <Toast ref={toastrRef} />
    </>
  );
};

export default ChangePasswordModal;
